/**
 * Agent activity log component (T158).
 *
 * Displays agent decisions with:
 * - Decision type icons
 * - Outcome status
 * - Expandable reasoning
 * - Timestamp
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  ListChecks,
  Minus,
  SkipForward,
  TrendingDown,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { type DecisionHistoryFilters, historyService } from '@/services/history';
import { agentService } from '@/services/agent';
import { toast } from '@/hooks/useToast';
import type { AgentDecision } from '@/stores/agentStore';
import { AgentActivityFilters } from '@/components/history/AgentActivityFilters';

const decisionTypeConfig: Record<
  AgentDecision['decisionType'],
  { icon: React.ComponentType<{ className?: string }>; color: string; label: string }
> = {
  buy: { icon: TrendingUp, color: 'text-green-500', label: 'Buy' },
  sell: { icon: TrendingDown, color: 'text-red-500', label: 'Sell' },
  hold: { icon: Minus, color: 'text-blue-500', label: 'Hold' },
  skip: { icon: SkipForward, color: 'text-muted-foreground', label: 'Skip' },
};

const outcomeConfig: Record<
  AgentDecision['outcome'],
  { icon: React.ComponentType<{ className?: string }>; color: string; bgColor: string }
> = {
  pending: {
    icon: Clock,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
  },
  executed: {
    icon: CheckCircle,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
  },
  rejected: {
    icon: XCircle,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/10',
  },
  failed: {
    icon: AlertCircle,
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
  },
  overridden: {
    icon: Minus,
    color: 'text-muted-foreground',
    bgColor: 'bg-muted',
  },
  awaiting_approval: {
    icon: Clock,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
  },
  queued: {
    icon: ListChecks,
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-500/10',
  },
};

interface DecisionItemProps {
  decision: AgentDecision;
  onAction?: (id: string, action: 'approve' | 'reject' | 'queue') => void;
}

function DecisionItem({ decision, onAction }: DecisionItemProps) {
  const [expanded, setExpanded] = useState(false);

  const typeConfig = decisionTypeConfig[decision.decisionType];
  const outcome = outcomeConfig[decision.outcome];
  const TypeIcon = typeConfig.icon;
  const OutcomeIcon = outcome.icon;

  return (
    <div className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
      <div className="flex items-start gap-3">
        {/* Decision type icon */}
        <div className={cn('p-2 rounded-lg', outcome.bgColor)}>
          <TypeIcon className={cn('h-5 w-5', typeConfig.color)} />
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold">{decision.symbol}</span>
            <span className={cn('text-sm', typeConfig.color)}>{typeConfig.label}</span>
            <span
              className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                outcome.bgColor,
                outcome.color
              )}
            >
              <OutcomeIcon className="h-3 w-3" />
              {decision.outcome}
            </span>
            <span className="text-xs text-muted-foreground ml-auto">
              {formatDate(decision.createdAt)}
            </span>
          </div>

          {/* Trade details */}
          {(decision.quantity || decision.price) && (
            <div className="text-sm text-muted-foreground mt-1">
              {decision.quantity && <span>{decision.quantity} shares</span>}
              {decision.quantity && decision.price && <span> @ </span>}
              {decision.price && <span>{formatCurrency(decision.price)}</span>}
              {decision.notional != null && (
                <span> ({formatCurrency(decision.notional)})</span>
              )}
            </div>
          )}

          {/* Confidence */}
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-muted-foreground">Confidence:</span>
            <div className="flex-1 max-w-24 h-2 rounded-full bg-muted overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all',
                  decision.confidence >= 0.8
                    ? 'bg-green-500'
                    : decision.confidence >= 0.5
                      ? 'bg-yellow-500'
                      : 'bg-red-500'
                )}
                style={{ width: `${decision.confidence * 100}%` }}
              />
            </div>
            <span className="text-xs font-medium">{Math.round(decision.confidence * 100)}%</span>
          </div>

          {/* Expandable reasoning */}
          {decision.reasoning && (
            <div className="mt-2">
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                {expanded ? 'Hide reasoning' : 'Show reasoning'}
              </button>
              {expanded && (
                <p className="mt-2 text-sm text-muted-foreground bg-muted/50 rounded p-2">
                  {decision.reasoning}
                </p>
              )}
            </div>
          )}

          {/* Action buttons for awaiting_approval decisions */}
          {decision.outcome === 'awaiting_approval' && onAction && (
            <div className="flex items-center gap-2 mt-3 pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                className="text-red-500 hover:text-red-600 hover:bg-red-50 gap-1"
                onClick={() => onAction(decision.id, 'reject')}
              >
                <XCircle className="h-3.5 w-3.5" />
                Reject
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-blue-500 hover:text-blue-600 hover:bg-blue-50 gap-1"
                onClick={() => onAction(decision.id, 'queue')}
              >
                <Clock className="h-3.5 w-3.5" />
                Queue for Open
              </Button>
              <Button
                variant="default"
                size="sm"
                className="gap-1"
                onClick={() => onAction(decision.id, 'approve')}
              >
                <CheckCircle className="h-3.5 w-3.5" />
                Approve & Execute
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function AgentActivityLog() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<DecisionHistoryFilters>({
    page: 1,
    pageSize: 10,
  });
  const page = filters.page || 1;
  const pageSize = filters.pageSize || 10;

  const handleDecisionAction = async (id: string, action: 'approve' | 'reject' | 'queue') => {
    try {
      if (action === 'approve') {
        await agentService.approveDecision(id);
        toast({ title: 'Decision Approved', description: 'Order will be executed', variant: 'success' });
      } else if (action === 'reject') {
        await agentService.rejectDecision(id);
        toast({ title: 'Decision Rejected', variant: 'default' });
      } else {
        await agentService.queueDecision(id);
        toast({ title: 'Decision Queued', description: 'Will execute at market open', variant: 'default' });
      }
      queryClient.invalidateQueries({ queryKey: ['agent', 'decisions'] });
      queryClient.invalidateQueries({ queryKey: ['agent', 'pending-approvals'] });
    } catch {
      toast({ title: 'Action Failed', variant: 'destructive' });
    }
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['agent', 'decisions', filters],
    queryFn: () => historyService.getAgentDecisions(filters),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <AlertCircle className="h-8 w-8 mx-auto text-red-500 mb-2" />
          <p className="text-muted-foreground">Failed to load agent activity</p>
        </CardContent>
      </Card>
    );
  }

  const totalPages = data?.totalPages || 1;
  const hasDecisions = !!data?.decisions.length;

  return (
    <div className="space-y-4">
      {/* Filters — always visible so users can change date range */}
      <AgentActivityFilters filters={filters} onFiltersChange={setFilters} />

      {/* Loading skeleton */}
      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="border rounded-lg p-4 animate-pulse">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 bg-muted rounded-lg" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-1/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                  <div className="h-2 bg-muted rounded w-1/3" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !hasDecisions && (
        <Card>
          <CardContent className="p-8 text-center">
            <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-lg font-medium">No agent activity found</p>
            <p className="text-sm text-muted-foreground mt-1">
              Try adjusting your filters or selecting a different time period
            </p>
          </CardContent>
        </Card>
      )}

      {/* Decision list */}
      {!isLoading && hasDecisions && (
        <>
          <div className="space-y-3">
            {data.decisions.map((decision) => (
              <DecisionItem key={decision.id} decision={decision} onAction={handleDecisionAction} />
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, data.total)} of{' '}
              {data.total} decisions
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setFilters({ ...filters, page: page - 1 })}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setFilters({ ...filters, page: page + 1 })}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default AgentActivityLog;
